import React, { useEffect, useState } from "react";
import { _get, _post } from "../../services/apiClient";
import { useNavigate, useParams } from "react-router-dom";
import { apiUrls } from "../../config/apiUrls.const";
import { getSecureItem } from "../../utils/LocalStorageManager";
import { allowOnlyNumbers } from "../../utils/AllowOnlyNumbers";
import Swal from "sweetalert2";
import { Button } from "react-bootstrap";
import MultiImageUpload from "../components/MultiImageUpload";
import { processAndMapImages } from "../../utils/ServiceAssetMapper";
import CreatableSelect from "react-select/creatable";

interface ServiceImage {
  id?: number;              // Database ID if it already exists
  file: File | string;       // File instance (new) or URL string (existing)
  isDefault: boolean;
  raw_path?: string;         // The underlying relative path from the DB
}

// React Select standard format structure
interface SelectOption {
  value: string;
  label: string;
}

export default function EditService() {
  const { id } = useParams();
  const navigate = useNavigate();
  const biz_code = getSecureItem("binfo")?.[0]?.biz_code;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [categoryId, setCategoryId] = useState<any | null>(null);
  const [subcategoryId, setSubcategoryId] = useState<any | null>(null);
  const [title, setTitle] = useState<string>("");
  const [price, setPrice] = useState<string>("");
  const [durationHr, setDurationHr] = useState<number | undefined>();
  const [durationMin, setDurationMin] = useState<number | undefined>();
  const [tagInput, setTagInput] = useState<string>("");
  
  // 💡 State for managing react-select tag configurations
  const [tags, setTags] = useState<SelectOption[]>([]);

  // Dynamic collection holding both old and new images
  const [images, setImages] = useState<ServiceImage[]>([]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Step 1: Fetch item data
        const itemRes: any = await _post(apiUrls.getItem, { item_id: id });
        if (!itemRes.data?.success) return;

        const item = itemRes.data.data;
        setTitle(item.title);
        setPrice(item.price);

        if (item.duration && item.duration.includes(':')) {
          const durationParts = item.duration.split(":");
          setDurationHr(Number(durationParts[0]));
          setDurationMin(Number(durationParts[1].replace("m", "")));
        } else if (item.duration) {
          setDurationMin(Number(item.duration.replace("m", "")));
        }

        setCategoryId(item.category_id);
        setSubcategoryId(item.subcategory_id);

        // 💡 Convert incoming backend array tags into react-select value objects
        if (item.tags) {
          // If the backend returns a real array: ["Jazz", "Style"]
          if (Array.isArray(item.tags)) {
            setTags(item.tags.map((t: string) => ({ value: t, label: t })));
          } 
          // Fallback if the backend returns a comma-separated string: "Jazz,Style"
          else if (typeof item.tags === "string") {
            setTags(
              item.tags.split(",").filter((t: string) => t.trim() !== "").map((t: string) => ({
                value: t.trim(),
                label: t.trim()
              }))
            );
          }
        }

        // Map existing images relation array into our unified UI state structure
        if (item.images && Array.isArray(item.images)) {
          const loadedImages: ServiceImage[] = item.images.map((img: any) => ({
            id: img.id,
            file: img.image_url, // Absolute URL for rendering previews
            isDefault: Boolean(img.is_default),
            raw_path: img.file_path // Kept so the backend knows this file hasn't changed
          }));
          setImages(loadedImages);
        }

        // Step 2: Load categorization for the item's group
        const catRes: any = await _post(apiUrls.getBusinessCategorization, { biz_code });
        const groupCats = catRes.data?.data[item.service_group] || [];
        setCategories(groupCats);

        // Step 3: Extract subcategories list from selected category
        const matchedCategory = groupCats.find((cat: any) => cat.category_id == item.category_id);
        setSubcategories(matchedCategory?.subcategories || []);

      } catch {
        Swal.fire("Error", "Failed to load service data.", "error");
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [id, biz_code]);

  const handleSubmit = async () => {
    setSaving(true);

    try {
      // Single point execution handles file microservice uploading and data normalization
      const finalProcessedImages = await processAndMapImages(images, biz_code);

      // 💡 Transform select tracking values back to a flat array string format matching Laravel validation expectations
      const flatTagsPayload = tags.map(t => t.value.trim()).filter(t => t !== "");

      const finalPayload = {
        item_id: id,
        biz_code: biz_code,
        category_id: categoryId,
        subcategory_id: subcategoryId,
        title: title,
        price: price,
        duration: durationHr ? `${durationHr}:${durationMin}m` : `${durationMin}m`,
        tags: flatTagsPayload, // Clean array payload matching validation rules
        images: finalProcessedImages 
      };

      const res: any = await _post(apiUrls.updateItem, finalPayload, {
        headers: { "Content-Type": "application/json" },
      });

      if (res.data?.success) {
        await Swal.fire("Success!", "Service updated successfully!", "success");
        navigate(-1);
      }
    } catch (err: any) {
      Swal.fire("Error", err.message || "Failed to process changes.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-3">Loading...</div>;

  return (
    <div className="container-fluid p-0">
      <h1 className="h3 mb-3">
        Edit Service
        <Button className="float-end" variant="dark" size="sm" onClick={() => navigate(-1)}>
          Back
        </Button>
      </h1>

      <div className="card">
        <div className="card-body">
          <div className="row">
            <div className="col-md-7">
              <div className="row">
                <div className="col-sm-6 mb-3">
                  <label className="form-label fw-bold">Category</label>
                  <select className="form-select" value={categoryId ?? ""} onChange={(e) => {
                    setCategoryId(Number(e.target.value));
                    const selected = categories.find(cat => cat.category_id == e.target.value);
                    setSubcategories(selected?.subcategories || []);
                    setSubcategoryId(null);
                  }}>
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.category_id}>{cat.category?.name_en}</option>
                    ))}
                  </select>
                </div>

                <div className="col-sm-6 mb-3">
                  <label className="form-label fw-bold">Subcategory</label>
                  <select className="form-select" value={subcategoryId ?? ""} onChange={(e) => setSubcategoryId(Number(e.target.value))}>
                    <option value="">Select Subcategory</option>
                    {subcategories.map((sub) => (
                      <option key={sub.id} value={sub.subcategory_id}>{sub.subcategory?.name_en}</option>
                    ))}
                  </select>
                </div>

                <div className="col-sm-12 mb-3">
                  <label className="form-label fw-bold">Title</label>
                  <input className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>

                <div className="col-sm-6 mb-3">
                  <label className="form-label fw-bold">Price</label>
                  <div className="input-group">
                    <input className="form-control" value={price} onKeyDown={allowOnlyNumbers} onChange={(e) => setPrice(e.target.value)} />
                    <span className="input-group-text">BDT</span>
                  </div>
                </div>

                <div className="col-sm-6 mb-3">
                  <label className="form-label fw-bold">Duration</label>
                  <div className="input-group">
                    <input className="form-control" value={durationHr ?? ""} onKeyDown={allowOnlyNumbers} onChange={(e) => setDurationHr(e.target.value ? Number(e.target.value) : undefined)} placeholder="hr" />
                    <span className="input-group-text">hr</span>
                    <input className="form-control" value={durationMin ?? ""} onKeyDown={allowOnlyNumbers} onChange={(e) => setDurationMin(e.target.value ? Number(e.target.value) : undefined)} placeholder="min" />
                    <span className="input-group-text">min</span>
                  </div>
                </div>

                {/* 💡 RE-ENGINEERED SEARCH CONTEXT TAG ENTRY BLOCK */}
                <div className="col-sm-12 mb-3">
                  <label className="form-label fw-bold mb-1">Search Context Tags</label>
                  <CreatableSelect
  isMulti
  value={tags}
  onChange={(newValue) => setTags(newValue ? [...newValue] : [])}
  placeholder="Type a tag and press Enter or Comma..."
  components={{ DropdownIndicator: null }}
  menuIsOpen={false}
  openMenuOnFocus={false}
  openMenuOnClick={false}
  
  // 💡 Controlled Input Properties
  inputValue={tagInput}
  onInputChange={(text, actionMeta) => {
    if (actionMeta.action === "input-change") {
      setTagInput(text);
    }
  }}
  
  onKeyDown={(e: any) => {
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault();
      const val = tagInput.trim();
      
      if (val) {
        if (!tags.some(o => o.value.toLowerCase() === val.toLowerCase())) {
          setTags([...tags, { value: val, label: val }]);
        }
        setTagInput(""); // 💡 Flush the controlled input state completely
      }
    }
  }}
  styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '38px',
                        borderColor: '#dee2e6',
                        boxShadow: 'none',
                        '&:hover': { borderColor: '#b1b8c0' }
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: '#6c757d',
                        borderRadius: '4px',
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: '#ffffff',
                        fontWeight: '500',
                        paddingLeft: '6px',
                        paddingRight: '6px'
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: '#ffffff',
                        ':hover': {
                          backgroundColor: '#dc3545',
                          color: '#ffffff',
                          borderRadius: '0 4px 4px 0'
                        },
                      }),
                    }}
/>
                </div>

              </div>
            </div>

            {/* CLEAN MODULARIZED MEDIA ASSET GALLERY SIDEBAR LAYOUT */}
            <div className="col-md-5 border-start ps-3">
              <label className="form-label fw-bold d-block mb-2">Service Media Assets Gallery</label>
              <MultiImageUpload
                indexId="edit_service_gallery"
                images={images}
                maxFiles={3}
                maxSizeMB={1}
                onChange={(updatedImages: ServiceImage[]) => setImages(updatedImages)}
              />
            </div>
          </div>

          <hr />
          <div className="text-end">
            <button className="btn btn-primary px-4" disabled={saving} onClick={handleSubmit}>
              {saving ? "Saving Changes..." : "Update Service Record"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}